import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, Shield } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { AuraMark, Wordmark } from "./AuraMark";
import { LangSwitcher } from "./LangSwitcher";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { scrollToId } from "./SmoothScroll";

const SECTIONS = ["about", "services", "why", "reviews", "contact", "faq"];

export const Navbar = () => {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (pathname !== "/") navigate("/", { state: { scrollTo: id } });
    else scrollToId(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-500 border-b ${scrolled || open ? "bg-[#050713]/75 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"}`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <AuraMark className="w-8 h-8 transition-transform duration-500 group-hover:rotate-[18deg]" />
          <Wordmark className="text-xl text-white" />
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          {SECTIONS.map((s) => (
            <button key={s} data-testid={`nav-link-${s}`} onClick={() => go(s)} className="nav-link text-sm text-slate-300 hover:text-white transition-colors">
              {t.nav[s]}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2 md:gap-3">
          <LangSwitcher />
          <AuthButtons user={user} t={t} logout={logout} className="hidden sm:flex" />
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen((o) => !o)} className="lg:hidden p-2 text-white" aria-label="menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden border-t border-white/10" data-testid="nav-mobile-menu">
            <div className="px-5 py-6 flex flex-col gap-4">
              {SECTIONS.map((s) => (
                <button key={s} data-testid={`nav-mobile-link-${s}`} onClick={() => go(s)} className="text-left font-display text-2xl text-white">
                  {t.nav[s]}
                </button>
              ))}
              <AuthButtons user={user} t={t} logout={logout} className="flex sm:hidden pt-2" onNav={() => setOpen(false)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const AuthButtons = ({ user, t, logout, className, onNav }) => (
  <div className={`items-center gap-2 ${className}`}>
    {user?.role === "admin" && (
      <Link to="/admin" onClick={onNav} data-testid="nav-admin-link" className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-2 text-xs text-slate-200 hover:border-sky-400/60 transition-colors">
        <Shield className="w-3.5 h-3.5" /> {t.nav.admin}
      </Link>
    )}
    <Link
      to={user ? "/area-membri" : "/accedi"}
      onClick={onNav}
      data-testid="nav-members-button"
      className="btn-chrome rounded-full px-4 py-2 text-xs font-semibold text-[#050713]"
    >
      {user ? t.nav.members : t.nav.login}
    </Link>
    {user && (
      <button data-testid="nav-logout-button" onClick={() => { logout(); onNav?.(); }} aria-label={t.nav.logout} className="p-2 text-slate-300 hover:text-white transition-colors">
        <LogOut className="w-4 h-4" />
      </button>
    )}
  </div>
);
